import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { canAccess } from "../hooks/usePermission";
// ✅ Dynamic API base URL — works locally & on HTTPS domain */

const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.magnirauniverse.com";

const milestoneStages = [
  { label: "Projects at 10% Completion", min: 10, max: 49 },
  { label: "Projects at 50% Completion", min: 50, max: 89 },
  { label: "Projects at 40% Completion", min: 90, max: 100 },
];

const generateMonthYearOptions = () => {
  const options = [];
  const today = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const label = date.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
    options.push(label);
  }
  return options;
};

const AfterBooking = () => {
  const [projects, setProjects] = useState([]);
  const [payments, setPayments] = useState([]);
  const [clientDetailsMap, setClientDetailsMap] = useState({});
  const navigate = useNavigate();
  const [editingDate, setEditingDate] = useState(null);
  const [dateDraft, setDateDraft] = useState("");
  const [savingDate, setSavingDate] = useState(false);
  const canEditDates =
    canAccess("clientDetail", "edit") ||
    canAccess("clientDetail", "clientDetailEdit");
  const canSaveDates =
    canAccess("clientDetail", "create") ||
    canAccess("clientDetail", "clientDetailSave");
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toLocaleString("default", { month: "long", year: "numeric" }),
  );
  const monthYearOptions = generateMonthYearOptions();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectRes, paymentRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/projects`),
        axios.get(`${BASE_URL}/api/payments`),
      ]);
      const allPayments = paymentRes.data || [];

      const filteredProjects = projectRes.data.filter((project) => {
        const totalPaid = allPayments
          .filter(
            (payment) =>
              String(payment.projectId?._id || payment.projectId) ===
              String(project._id),
          )
          .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);

        return totalPaid >= 1 && !project.isArchived;
      });
      setProjects(filteredProjects);
      setPayments(paymentRes.data || []);
      await fetchClientDetails(filteredProjects);
    } catch (err) {
      console.error("❌ Error loading data:", err.message);
    }
  };

  const fetchClientDetails = async (projectList) => {
    try {
      const detailMap = {};
      for (let project of projectList) {
        const res = await axios.get(
          `${BASE_URL}/api/client-details/${encodeURIComponent(project.name)}`,
        );
        detailMap[project._id] = res.data?.clientDetails || res.data || {};
      }
      setClientDetailsMap(detailMap);
    } catch (error) {
      console.error("❌ Failed to fetch client details:", error);
    }
  };

  const toDateInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const saveProjectDate = async (project, field) => {
    if (!canEditDates || !canSaveDates || savingDate) return;
    setSavingDate(true);
    try {
      const current = clientDetailsMap[project._id] || {};
      const updated = { ...current, [field]: dateDraft };
      const response = await axios.post(`${BASE_URL}/api/client-details/save`, {
        projectName: project.name,
        clientDetails: updated,
      });
      const saved = response.data?.clientDetails || updated;
      setClientDetailsMap((previous) => ({
        ...previous,
        [project._id]: saved,
      }));
      setEditingDate(null);
    } catch (error) {
      console.error("Date update failed:", error?.response?.data || error);
      alert(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Date could not be saved.",
      );
    } finally {
      setSavingDate(false);
    }
  };

  const editableDate = (project, field, value) => {
    const active =
      editingDate?.projectId === project._id && editingDate?.field === field;
    if (active)
      return (
        <span
          style={{
            display: "inline-flex",
            gap: 4,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="date"
            autoFocus
            value={dateDraft}
            disabled={savingDate}
            onChange={(event) => setDateDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") saveProjectDate(project, field);
              if (event.key === "Escape") setEditingDate(null);
            }}
          />
          <button
            type="button"
            disabled={savingDate}
            onClick={() => saveProjectDate(project, field)}
          >
            Save
          </button>
          <button
            type="button"
            disabled={savingDate}
            onClick={() => setEditingDate(null)}
          >
            Cancel
          </button>
        </span>
      );
    return (
      <span
        onDoubleClick={() => {
          if (!canEditDates || !canSaveDates) return;
          setDateDraft(toDateInput(value));
          setEditingDate({ projectId: project._id, field });
        }}
        title={
          canEditDates && canSaveDates ? "Double-click to edit date" : undefined
        }
        style={{
          cursor: canEditDates && canSaveDates ? "pointer" : "default",
          borderBottom:
            canEditDates && canSaveDates ? "1px dashed #888" : "none",
        }}
      >
        {value ? new Date(value).toLocaleDateString("en-IN") : "N/A"}
      </span>
    );
  };

  // Show aging only for a genuine project start date, never the record creation date.
  const getProjectStart = (project, client) => {
    const raw =
      client.projectStartDate ||
      client.startDate ||
      project.projectStartDate ||
      project.startDate;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const getProjectAging = (start) => {
    if (!start) return "N/A";
    const today = new Date();
    const from = Date.UTC(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
    );
    const to = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    if (from > to) return "Not started";
    return `${Math.floor((to - from) / 86400000)} days`;
  };

  const getTotalPaidForProject = (projectId) =>
    payments
      .filter((p) => p.projectId === projectId)
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const getProjectStage = (project) => {
    const paid = getTotalPaidForProject(project._id);
    const total = Number(project.finalAmount || project.quoteAmount || 1);
    const percent = Math.round((paid / total) * 100);
    for (let stage of milestoneStages) {
      if (percent >= stage.min && percent <= stage.max) {
        return stage.label;
      }
    }
    return null;
  };

  const groupedProjects = (label) =>
    projects.filter((p) => getProjectStage(p) === label);

  const getMonthlyRevenue = () => {
    const filtered = payments.filter((p) => {
      const date = new Date(p.date);
      const formatted = date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      return formatted === selectedMonth;
    });
    return filtered.reduce((sum, p) => sum + Number(p.amount || 0), 0);
  };

  return (
    <div style={{ padding: "30px", background: "#f5f7fa", minHeight: "100vh" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "30px",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: "8px 16px",
            backgroundColor: "#0A5F59",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
          }}
        >
          ⬅ Back
        </button>

        <h2
          style={{
            fontSize: "28px",
            fontWeight: "bold",
            margin: 0,
            flex: 1,
            textAlign: "center",
          }}
        >
          🔄 Active Projects by Completion Milestone
        </h2>

        <div style={{ width: "80px" }}></div>
      </div>

      <div style={{ display: "flex", gap: "40px", flexWrap: "wrap" }}>
        {milestoneStages.map((stage) => (
          <div key={stage.label} style={{ flex: 1, minWidth: "320px" }}>
            <h3
              style={{
                textAlign: "center",
                marginBottom: "20px",
                fontSize: "18px",
              }}
            >
              {stage.label}
            </h3>

            {groupedProjects(stage.label).map((proj) => {
              const totalPaid = getTotalPaidForProject(proj._id);
              const totalAmount = Number(
                proj.finalAmount || proj.quoteAmount || 0,
              );
              const percentComplete = Math.round(
                (totalPaid / totalAmount) * 100,
              );
              const client = clientDetailsMap[proj._id] || {};
              const projectStart = getProjectStart(proj, client);

              return (
                <div
                  key={proj._id}
                  style={{
                    borderRadius: "14px",
                    backgroundColor: "#ffffff",
                    padding: "0",
                    marginBottom: "25px",
                    overflow: "hidden",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.06)",
                    transition: "transform 0.2s ease",
                    border: "1px solid #dbe0e5",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-4px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#0A5F59",
                      color: "#fff",
                      padding: "15px 20px",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    {proj.name}
                  </div>

                  <div style={{ padding: "20px" }}>
                    <div style={{ fontSize: "14px", color: "#333" }}>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Project Value:</strong>{" "}
                        <span style={{ fontWeight: "bold" }}>
                          ₹ {(totalAmount / 100000).toFixed(2)}L
                        </span>
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Collected:</strong> ₹{" "}
                        {totalPaid.toLocaleString("en-IN")}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Project Start Date:</strong>{" "}
                        {editableDate(proj, "projectStartDate", projectStart)}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Project Aging:</strong>{" "}
                        {getProjectAging(projectStart)}
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Expected Completion:</strong>{" "}
                        {editableDate(
                          proj,
                          "expectedCompletionDate",
                          client.expectedCompletionDate,
                        )}
                      </div>
                      <div>
                        <strong>Completion:</strong>{" "}
                        <span
                          style={{
                            color:
                              percentComplete >= 100 ? "#08723E" : "#F39C12",
                            fontWeight: "600",
                          }}
                        >
                          {percentComplete}%
                        </span>
                      </div>
                    </div>

                    <div
                      style={{
                        marginTop: "16px",
                        display: "flex",
                        gap: "10px",
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          backgroundColor: "#E8F0FC",
                          color: "#0A5FBA",
                          padding: "6px 14px",
                          borderRadius: "20px",
                          fontSize: "13px",
                          fontWeight: "bold",
                        }}
                      >
                        Softvolute Buildcon LLP
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div
        style={{
          marginTop: "50px",
          padding: "25px",
          border: "1px solid #ddd",
          borderRadius: "12px",
          background: "#fff",
          maxWidth: "600px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        }}
      >
        <h3 style={{ fontSize: "20px", marginBottom: "15px" }}>
          📈 Monthly Revenue Achieved
        </h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
          }}
        >
          <div>
            <p style={{ margin: "0", fontWeight: "500" }}>
              💰 Revenue Achieved:
            </p>
            <strong style={{ fontSize: "18px" }}>
              ₹ {getMonthlyRevenue().toLocaleString("en-IN")}
            </strong>
          </div>
          <div>
            <label htmlFor="month" style={{ fontWeight: "500" }}>
              Month:
            </label>
            <select
              id="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                padding: "6px 10px",
                marginLeft: "10px",
                borderRadius: "8px",
                border: "1px solid #ccc",
              }}
            >
              {monthYearOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AfterBooking;
